import { faCheck, faChevronRight, faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type FC } from 'react';

import Debug from '@/components/Debug';
import MemberEligibilityBadges from '@/components/MemberEligibilityBadges';
import SessionAvailabilityPill from '@/components/SessionAvailabilityPill';
import type { Props } from '@/components/TreatmentGroupCard/types';
import TreatmentGroupEnrollmentButton from '@/components/TreatmentGroupEnrollmentButton';
import { DAY_LABELS } from '@/constants/dayOfWeek';
import { Card, Pill, Typography } from '@/ui';
import { cn } from '@/ui/lib/utils';
import { formatCalendarDate, formatTimeOfDay, getTimeZoneAbbreviation } from '@/utils/dates';
import { formatCounselorsForCard, sessionLetterMap } from '@/utils/treatmentGroupSessions';

const TreatmentGroupCard: FC<Props> = ({
  group,
  isDimmed,
  isPending,
  memberFirstName = 'this member',
  onEnroll,
  onRemove,
  onSelect
}) => {
  const { member_eligibility } = group;
  const isEnrolled = member_eligibility.is_enrolled;
  const showEnrollAction = !isEnrolled && !!onEnroll;
  const showRemoveAction = isEnrolled && !!onRemove && !!member_eligibility.membership_id;

  // Letters, not ids -- matches how the server stores/reports a partial
  // membership's session selection (see sessionLetterMap's own comment).
  const letterMap = sessionLetterMap(
    group.sessions.map((session) => ({
      id: session.id,
      dayOfWeek: session.day_of_week,
      startTime: session.start_time
    }))
  );

  const handleEnroll = () => onEnroll?.(group);
  const handleRemove = () => onRemove?.(group);

  // TreatmentGroupEnrollmentButton's onClick contract is a plain () => void,
  // so it can't stop its own propagation -- the whole card is clickable, so
  // this stops the click from also firing onSelect's navigation.
  const stopPropagation = (event: React.MouseEvent) => event.stopPropagation();

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    onSelect?.(group);
  };

  return (
    <Card
      className={cn(
        'relative space-y-3',
        isDimmed && 'opacity-60',
        onSelect &&
          'cursor-pointer hover:border-uniform-gray-300 dark:hover:border-uniform-gray-600'
      )}
      onClick={onSelect ? () => onSelect(group) : undefined}
      onKeyDown={onSelect ? handleKeyDown : undefined}
      role={onSelect ? 'button' : undefined}
      tabIndex={onSelect ? 0 : undefined}
      aria-label={onSelect ? `View ${group.name}` : undefined}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <Typography variant="subheading">{group.name}</Typography>
          <Typography variant="body" muted>
            {formatCounselorsForCard(group.current_sessions)}
          </Typography>
        </div>
        {onSelect && (
          <FontAwesomeIcon icon={faChevronRight} className="mt-1 shrink-0 text-uniform-gray-400" />
        )}
      </div>

      <div className="flex flex-wrap items-center gap-1">
        {group.current_sessions.map((session) => {
          const letters = member_eligibility.partial_session_letters;
          // Once enrolled, which sessions this person actually attends
          // matters more than raw availability -- a partial-session member
          // shouldn't have their other sessions read as "theirs" too.
          const isAttendingSession =
            isEnrolled && (letters === null || letters.includes(letterMap.get(session.id) ?? ''));
          const dimNotAttending = isEnrolled && !isAttendingSession;
          return (
            <SessionAvailabilityPill
              key={session.id}
              isWithinPersonAvailability={session.is_within_person_availability}
              memberFirstName={memberFirstName}>
              <Pill
                size="sm"
                variant={isAttendingSession ? 'filled' : 'outline'}
                color={
                  isAttendingSession
                    ? 'primary'
                    : isEnrolled
                      ? 'neutral'
                      : session.is_within_person_availability
                        ? 'primary'
                        : 'neutral'
                }
                icon={isAttendingSession ? faCheck : undefined}
                className={cn(
                  'inline leading-none',
                  dimNotAttending && 'border-dashed opacity-60'
                )}>
                {DAY_LABELS[session.display_day_of_week]} ·{' '}
                {formatTimeOfDay(session.display_start_time)}{' '}
                {getTimeZoneAbbreviation(session.display_timezone)}
              </Pill>
            </SessionAvailabilityPill>
          );
        })}
      </div>

      <hr className="border-uniform-gray-200 dark:border-uniform-gray-700" />

      <div className="flex flex-wrap items-center gap-2">
        <span className="flex items-center gap-1 text-uniform-gray-500">
          <FontAwesomeIcon icon={faUser} className="h-3 w-3" />
          <Typography variant="body" className="text-sm">
            {group.active_member_count}/{group.capacity}
          </Typography>
        </span>
        <Pill
          size="sm"
          variant="subtle"
          color={group.accepting_new_patients ? 'primary' : 'neutral'}
          className="inline leading-none">
          {group.accepting_new_patients ? 'Accepting' : 'Not accepting'}
        </Pill>
      </div>

      <MemberEligibilityBadges
        conflictOfInterestReason={member_eligibility.conflict_of_interest_reason}
        hasConflictOfInterest={member_eligibility.has_conflict_of_interest}
        ineligibilityReason={member_eligibility.ineligibility_reason}
        isCredentialed={member_eligibility.is_credentialed}
        isEligible={member_eligibility.is_eligible}
      />

      {isEnrolled && (
        <div className="flex items-center justify-between gap-2">
          {member_eligibility.membership_start_on && (
            <Typography variant="body" muted className="text-sm">
              Since {formatCalendarDate(member_eligibility.membership_start_on)}
            </Typography>
          )}
          {showRemoveAction && (
            <div onClick={stopPropagation}>
              <TreatmentGroupEnrollmentButton
                isEnrolled
                isPending={!!isPending}
                onEnroll={handleEnroll}
                onRemove={handleRemove}
              />
            </div>
          )}
        </div>
      )}

      {showEnrollAction && (
        <div className="flex justify-end" onClick={stopPropagation}>
          <TreatmentGroupEnrollmentButton
            isEnrolled={false}
            isPending={!!isPending}
            onEnroll={handleEnroll}
            onRemove={handleRemove}
          />
        </div>
      )}

      <div onClick={stopPropagation}>
        <Debug object={group} />
      </div>
    </Card>
  );
};

export default TreatmentGroupCard;
